import {
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  MixpanelEvent,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  tabLabelerEnumerated,
  useMixpanel,
  usePersistedTabs,
  usePortal,
  useTabs,
  type TabProps,
} from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';
import { useBlotterTableTabCloning } from '../../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import { RECON_MISMATCHES_BLOTTER_PREFIX } from '../tokens';
import { buildDefaultReconMismatchesTabs, buildNewReconMismatchTabDefaults } from './defaults';
import { ReconMismatchesBlotter } from './ReconMismatchesBlotter';
import type { BlotterTableReconMismatchesFilter } from './useReconMismatchesFilter';

export interface ReconMismatchesTabProps extends TabProps {
  defaultFilter?: BlotterTableReconMismatchesFilter;
}

function getBlotterID(tabID: string | undefined) {
  return `${RECON_MISMATCHES_BLOTTER_PREFIX}/${tabID}`;
}

const tabLabeler = tabLabelerEnumerated('Mismatches');

export const ReconMismatches = () => {
  const mixpanel = useMixpanel();
  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);

  const handleSelect = useCallback(() => setInitialFiltersOpen(false), []);
  const handleAdd = useCallback(() => setInitialFiltersOpen(true), []);

  const defaultInitialItems = useMemo(() => buildDefaultReconMismatchesTabs(), []);
  const newTabDefaults = useMemo(() => buildNewReconMismatchTabDefaults(), []);

  const persistedTabs = usePersistedTabs<ReconMismatchesTabProps>(RECON_MISMATCHES_BLOTTER_PREFIX, {
    defaultInitialItems,
    defaultInitialSelectedIndex: 0,
    onSelect: handleSelect,
  });

  const tabs = useTabs<ReconMismatchesTabProps>({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
    allowClosingLastTab: false,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });

  const handleCloneTab = useCallback(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    tabs.clone(tabs.selectedIndex);
  }, [mixpanel, tabs]);

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  return (
    <Tabs {...tabs} h="100%" size={TabSize.Large} data-testid="recon-mismatches-blotter">
      <TabList isBordered rightItems={<Box ref={filtersContainerRef} />}>
        {tabs.items.map((tab, idx) => (
          <Tab key={idx} {...tab} />
        ))}
      </TabList>
      <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
        {tabs.items.map(tab => (
          <AccordionGroup key={tab.id}>
            <ReconMismatchesBlotter
              key={tab.id}
              blotterID={getBlotterID(tab.id)}
              tabLabel={tab.label}
              defaultFilter={tab.defaultFilter ?? newTabDefaults.defaultFilter}
              onCloneTab={handleCloneTab}
              initialIsOpen={initialFiltersOpen}
            />
          </AccordionGroup>
        ))}
      </TabPanels>
    </Tabs>
  );
};
